import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useStore } from 'react-redux';
import useAdvertisementHook from '../../../hooks/advertisement-hook';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import type { RootState } from '../../../store/store';
import RemoteMissionsPopup from './RemoteMissionsPopup';

export const REMOTE_MISSIONS_ONBOARDING_CODE = 'RemoteMissionsOnboarding';

// Mounted by account so neither loaded preferences nor pending callbacks can leak
// across shoppers. Focus, not mission-list updates, defines a new visit.
const RemoteMissionsIntroduction = ({ blocked }: { blocked: boolean }) => {
  const tracking = useAdvertisementHook();
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const lightBox = useAppSelector(state => state.lightBox);
  const [visit, setVisit] = useState(0);
  const focused = useRef(false);
  const shown = useRef(false);
  const optedOut = useRef(false);
  const ownedContent = useRef<React.ReactElement | undefined>(undefined);
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;

  const dismissOwnedPopup = useCallback(() => {
    // Another flow may replace the global lightbox in the same event.
    if (ownedContent.current && store.getState().lightBox.content === ownedContent.current) {
      dispatch(lightBoxActions.setVisibility(false));
      dispatch(lightBoxActions.setOnClose(undefined));
      dispatch(lightBoxActions.setContent(<></>));
    }
    ownedContent.current = undefined;
  }, [dispatch, store]);

  useEffect(() => {
    if (blocked) dismissOwnedPopup();
  }, [blocked, dismissOwnedPopup]);

  useFocusEffect(useCallback(() => {
    focused.current = true;
    shown.current = false;
    setVisit(value => value + 1);
    return () => {
      focused.current = false;
      dismissOwnedPopup();
    };
  }, [dismissOwnedPopup]));

  useEffect(() => {
    if (!focused.current || shown.current || optedOut.current || !tracking.loaded || blocked) return;
    const current = store.getState().lightBox;
    if (current.visible || current.secondVisible || current.isLoading) return;
    if (!tracking.showAdvertisement(REMOTE_MISSIONS_ONBOARDING_CODE)) return;

    // ProfileManager transitions between required forms after 300 ms. Wait for
    // that handoff to settle instead of inserting an intro between two forms.
    const timer = setTimeout(() => {
      const current = store.getState().lightBox;
      if (!focused.current || blockedRef.current || shown.current || current.visible || current.secondVisible || current.isLoading) return;
      shown.current = true;
      const content = <RemoteMissionsPopup onClose={dontShowAgain => {
        if (!focused.current || ownedContent.current !== content) return;
        if (dontShowAgain) {
          optedOut.current = true;
          tracking.dontShowAgain(REMOTE_MISSIONS_ONBOARDING_CODE);
        }
      }} />;
      ownedContent.current = content;
      dispatch(lightBoxActions.setOnClose(undefined));
      dispatch(lightBoxActions.setContent(content));
      dispatch(lightBoxActions.setVisibility(true));
    }, 400);
    return () => clearTimeout(timer);
  }, [blocked, dispatch, lightBox.visible, lightBox.secondVisible, lightBox.isLoading, store, tracking, visit]);

  return null;
};

export default RemoteMissionsIntroduction;
